// src/components/membership/ScheduledPaymentTooltip.tsx
import React, { useState, useEffect } from "react";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { useTheme } from "../../context/ThemeContext";
import { captureError } from "../../utils/sentry";
import {
  getCardBackground,
  getPrimaryText,
  getMutedText,
  getLightBorder,
  getTableRowStyles,
} from "../../utils/themeUtils";
import { statusLabel } from "../../utils/statusLabel";

interface ScheduledPaymentTooltipProps {
  payment: any;
  onClose?: () => void;
  onCancel?: (payment: any) => void;
  isAdminOrStaff?: boolean;
}

const ScheduledPaymentTooltip: React.FC<ScheduledPaymentTooltipProps> = ({
  payment,
  onClose,
  onCancel,
  isAdminOrStaff = false,
}) => {
  const { isDarkMode } = useTheme();
  const [, setSettings] = useState<any>(null);

  // Helper function to get payment method badge color
  const getPaymentMethodColor = (method: string): string => {
    const colors: { [key: string]: string } = {
      'Cash': isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      'Card': isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
      'Check': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Cheque': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
    };
    return colors[method] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (error) {
        captureError(error, { component: 'ScheduledPaymentTooltip', operation: 'parseSettings' });
      }
    }
  }, []);

  return (
    <div
      className={`fixed z-50 ${getCardBackground(
        isDarkMode
      )} text-sm ${getPrimaryText(isDarkMode)} border ${getLightBorder(
        isDarkMode
      )} rounded shadow-lg top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-opacity duration-200 w-[800px] max-w-[90vw] max-h-[90vh] flex flex-col`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="overflow-auto flex-1">
        {/* Close button - hidden on desktop since clicking outside closes modal */}
        {onClose && (
          <button
            onClick={onClose}
            className={`hidden absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center font-bold text-white shadow-md ${
              isDarkMode
                ? "bg-gray-600 hover:bg-gray-500"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            ✕
          </button>
        )}

        {/* Payment Header */}
        <div className={`${getCardBackground(isDarkMode)} px-4 py-3 border-b ${getLightBorder(isDarkMode)}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-lg font-bold ${getPrimaryText(isDarkMode)}`}>
              Scheduled Payment Details
            </h3>
            {/* Action Buttons */}
            {onCancel && isAdminOrStaff && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(payment);
                  }}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    isDarkMode
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                >
                  Cancel Payment
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Amount</span>
              <p className={`${getPrimaryText(isDarkMode)} font-medium text-lg`}>
                ${payment?.amount?.toFixed(2)}
              </p>
            </div>
            <div>
              <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Scheduled Date</span>
              <p className={`${getPrimaryText(isDarkMode)} font-medium`}>
                {payment?.scheduled_date ? formatDateOnly(payment.scheduled_date) : 'N/A'}
              </p>
            </div>
          </div>
          <div className="mt-2">
            <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Payment Method</span>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getPaymentMethodColor('Card')}`}>
                Card
              </span>
            </div>
          </div>
        </div>

        {/* Payment Details Section */}
        <div className={`${getCardBackground(isDarkMode)} px-4 py-3 border-b ${getLightBorder(isDarkMode)}`}>
          <div className="grid grid-cols-2 gap-4">
            {/* Description */}
            <div className="col-span-2">
              <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Description</span>
              <p className={`${getPrimaryText(isDarkMode)} font-medium`}>
                {payment?.description || payment?.note || 'Scheduled payment'}
              </p>
            </div>
            {/* Payment ID */}
            {payment?.id && (
              <div>
                <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Payment ID</span>
                <p className={`${getPrimaryText(isDarkMode)} font-medium text-xs break-all`}>
                  {payment.id}
                </p>
              </div>
            )}
            {/* Created Date */}
            {payment?.created_at && (
              <div>
                <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Created</span>
                <p className={`${getPrimaryText(isDarkMode)} font-medium`}>
                  {new Date(payment.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
            {/* Status */}
            {payment?.status && (
              <div className="col-span-2">
                <span className={`text-xs font-semibold ${getMutedText(isDarkMode)} uppercase`}>Status</span>
                <p className={`${getPrimaryText(isDarkMode)} font-medium`}>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    payment.status === 'scheduled'
                      ? isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {statusLabel(payment.status)}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <table
          className={`min-w-full text-left border ${getLightBorder(
            isDarkMode
          )} border-collapse table-auto`}
        >
          <thead>
            <tr className={`${getTableRowStyles(isDarkMode).header} text-xs`}>
              <th
                className={`border ${getLightBorder(isDarkMode)} px-3 py-2`}
                colSpan={2}
              >
                Additional Information
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className={getTableRowStyles(isDarkMode).row}>
              <td
                className={`border ${getLightBorder(
                  isDarkMode
                )} px-3 py-2 font-medium w-1/3`}
              >
                Payment Method
              </td>
              <td className={`border ${getLightBorder(isDarkMode)} px-3 py-2`}>
                Card ending in •••• (automated)
              </td>
            </tr>
            <tr className={getTableRowStyles(isDarkMode).row}>
              <td
                className={`border ${getLightBorder(
                  isDarkMode
                )} px-3 py-2 font-medium`}
              >
                Amount
              </td>
              <td className={`border ${getLightBorder(isDarkMode)} px-3 py-2 font-semibold`}>
                ${payment?.amount?.toFixed(2)}
              </td>
            </tr>
            <tr className={getTableRowStyles(isDarkMode).row}>
              <td
                className={`border ${getLightBorder(
                  isDarkMode
                )} px-3 py-2 font-medium`}
              >
                Scheduled For
              </td>
              <td className={`border ${getLightBorder(isDarkMode)} px-3 py-2`}>
                {payment?.scheduled_date ? formatDateOnly(payment.scheduled_date, 'en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </td>
            </tr>
            <tr className={getTableRowStyles(isDarkMode).row}>
              <td
                className={`border ${getLightBorder(
                  isDarkMode
                )} px-3 py-2 font-medium`}
              >
                Description / Note
              </td>
              <td className={`border ${getLightBorder(isDarkMode)} px-3 py-2`}>
                {payment?.description || payment?.note || 'No description provided'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduledPaymentTooltip;
